import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, X, MousePointerClick, ClipboardList } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ServiceCard from '../components/ServiceCard';
import { FadeUp } from '../components/Reveal';
import Marquee from '../components/Marquee';
import { services, normsMarquee, images, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

function ServiceModal({ service, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-navy-deep/90 p-4 backdrop-blur-sm"
      data-testid="service-modal"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-line bg-[var(--surface)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="service-modal-close"
          aria-label="Fechar detalhes do serviço"
          className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-navy/80 text-white transition-colors hover:bg-gold hover:text-navy-deep"
        >
          <X size={18} />
        </button>
        <div className="relative aspect-video w-full overflow-hidden">
          <img src={service.image} alt={service.name} className="h-full w-full object-cover" data-testid="service-modal-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" aria-hidden="true" />
          <span className="absolute bottom-4 left-5 rounded-full border border-white/25 bg-navy/80 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-widest text-gold-light backdrop-blur-sm">
            {service.category}
          </span>
        </div>
        <div className="p-7 md:p-9">
          <h3 className="font-display text-2xl font-bold leading-snug text-navy dark:text-white">{service.name}</h3>
          <p className="mt-3 leading-relaxed text-muted-c">{service.shortDescription}</p>
          <p className="mt-3 border-t border-line pt-3 text-sm leading-relaxed text-muted-c">{service.detailedDescription}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {service.norms.map((n) => (
              <span key={n} className="chip">{n}</span>
            ))}
          </div>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to={`/simulador?demanda=${encodeURIComponent(service.demand)}`}
              data-testid="service-modal-simulate"
              className="btn-gold flex-1"
            >
              Simular este serviço <ArrowRight size={16} />
            </Link>
            <a
              href={waUrl(`Olá, Eng. Amilcar! Acessei o site do Grupo Majo e tenho interesse em: ${service.name}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="service-modal-whatsapp"
              className="btn-outline flex-1 text-navy dark:text-white"
            >
              <MessageCircle size={16} /> Falar com o engenheiro
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Servicos() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : '';
    const onEsc = (e) => e.key === 'Escape' && setActive(null);
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onEsc);
    };
  }, [active]);

  return (
    <>
      <PageHeader
        index="03"
        eyebrow="Áreas de Atuação"
        title="Oito frentes técnicas sob uma mesma responsabilidade"
        description="Projetos, laudos, ART, inspeção predial, instalações elétricas, segurança do trabalho, regularização e arquitetura — sempre com conformidade normativa e respaldo no CREA. Clique em um serviço para ver detalhes e imagens."
        image={images.crane}
      />
      <Marquee items={normsMarquee} />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="services-grid-section">
        <FadeUp>
          <p className="mb-8 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-semibold text-navy dark:text-gold-light" data-testid="services-click-hint">
            <MousePointerClick size={14} className="text-gold" /> Clique em qualquer serviço para abrir a ficha com foto e detalhes
          </p>
        </FadeUp>
        <div className="grid gap-6 md:grid-cols-2">
          {services.map((s, i) => (
            <FadeUp key={s.id} delay={(i % 2) * 0.08}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setActive(s)}
                onKeyDown={(e) => e.key === 'Enter' && setActive(s)}
                className="h-full cursor-pointer"
                data-testid={`service-open-${s.id}`}
              >
                <ServiceCard service={s} detailed />
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.08}>
            <Link
              to="/pmp-empresas"
              data-testid="service-card-pmp"
              className="group card-hover relative flex h-full flex-col overflow-hidden rounded-2xl bg-navy p-7 text-white"
            >
              <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
              <span className="pointer-events-none absolute -right-2 -top-5 select-none font-display text-7xl font-extrabold text-white/10" aria-hidden="true">09</span>
              <div className="relative">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gold text-navy-deep">
                  <ClipboardList size={22} strokeWidth={1.8} />
                </div>
                <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-gold-light">PMP Empresarial</p>
                <h3 className="mt-2 font-display text-lg font-bold leading-snug">PMP — Plano de Manutenção Preventiva</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">
                  Programa contínuo de manutenção predial para empresas e condomínios: 8 disciplinas técnicas, mapa de atendimento e SLA de até 36 horas para ocorrências de Nível I.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="chip !border-white/20 !text-white/70">NBR 5674</span>
                  <span className="chip !border-white/20 !text-white/70">SLA 36h</span>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-wider text-gold-light">
                  Conhecer o PMP <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-24 text-white" data-testid="services-cta">
        <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <FadeUp>
            <p className="eyebrow justify-center">Documentos e laudos a partir de R$ 650,00 + taxas do conselho</p>
            <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight md:text-5xl">
              Sua demanda técnica começa com uma conversa
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-white/65">
              Descreva o serviço no simulador e receba uma proposta com escopo, prazo e emissão de ART — direto no WhatsApp do engenheiro.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3.5 sm:flex-row">
              <Link to="/simulador" data-testid="services-cta-simulator" className="btn-gold">
                Simular Orçamento <ArrowRight size={16} />
              </Link>
              <a
                href={waUrl(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-cta-whatsapp"
                className="btn-outline text-white"
              >
                <MessageCircle size={16} /> Falar com o Engenheiro
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {active && <ServiceModal service={active} onClose={() => setActive(null)} />}
    </>
  );
}
